import {
  EvaluationAvailableToRelateProforma,
  EvaluationAvailableToRelateProformaResponse
} from '@module/evaluation/model/EvaluationAvailableToRelateProformaResponse';
import { EvaluationResponse } from '@module/evaluation/model/EvaluationResponse';
import { SaveEvaluationValuesForm } from '@module/evaluation/model/SaveEvaluationValuesForm';
import { EvaluationRecalculateDTO } from '@state';
import { mainRestApi } from '@state/stores/restApi/rest-api';
import {
  addSelectedValueToLinkedPDAs,
  getSelectedPDAsIds
} from '../apiHelpers';
import { evaluationApi } from '../api';

export const proformaApi = evaluationApi.injectEndpoints({
  endpoints: (build) => ({
    fetchLinkedPDAs: build.query<
      EvaluationAvailableToRelateProforma,
      { evaluationId: number; relationIds: string[] }
    >({
      query: ({ evaluationId }) => ({
        url: `/${evaluationId}/load-available-to-relate-proformas`,
        method: 'GET'
      }),
      transformResponse: (
        response: EvaluationAvailableToRelateProformaResponse,
        meta,
        arg
      ) => addSelectedValueToLinkedPDAs(response, arg.relationIds)
    }),

    submitLinkedPDAs: build.mutation<
      EvaluationResponse,
      {
        evaluationFromData: Omit<SaveEvaluationValuesForm, 'relationIds'>;
        selectedLinkedPDAs: EvaluationAvailableToRelateProforma;
        /** When true, skip invalidating EVALUATION so the caller can refetch after a follow-up step (e.g. recalculate). */
        deferEvaluationInvalidate?: boolean;
      }
    >({
      query: ({ evaluationFromData, selectedLinkedPDAs }) => {
        const saveEvaluationValuesForm: SaveEvaluationValuesForm = {
          id: evaluationFromData.id,
          currency: evaluationFromData.currency,
          rate: evaluationFromData.rate,
          rateEur: evaluationFromData.rateEur,
          terminalId: evaluationFromData.terminalId,
          berthId: evaluationFromData.berthId,
          invoiceNumber: evaluationFromData.invoiceNumber,
          invoiceDate: evaluationFromData.invoiceDate,
          relationIds: getSelectedPDAsIds(selectedLinkedPDAs),
          proformaGroupId: evaluationFromData.proformaGroupId ?? null
        };

        return {
          url: '/save-evaluation-values',
          data: saveEvaluationValuesForm,
          method: 'POST'
        };
      },
      invalidatesTags: (res, _, arg) => {
        if (!res) {
          return ['EVALUATION'];
        }
        if (arg.deferEvaluationInvalidate) {
          return [];
        }
        return [{ type: 'EVALUATION', id: arg.evaluationFromData.id }];
      },
      async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            mainRestApi.util.invalidateTags([
              { type: 'SECTION_PORT_EXPENSES', id: data.proforma.port.id }
            ])
          );
        } catch {
          // Only invalidate cross-API tags on success
        }
      }
    }),

    recalculateSelectedEvaluations: build.mutation<
      EvaluationResponse,
      EvaluationRecalculateDTO
    >({
      query: (data) => ({
        url: '/recalculate-expense-evaluations',
        method: 'POST',
        data
      }),
      invalidatesTags: (res, _err, data) =>
        res ? [{ type: 'EVALUATION', id: data.evaluationId }] : ['EVALUATION']
    })
  }),
  overrideExisting: false
});

export const {
  useFetchLinkedPDAsQuery,
  useSubmitLinkedPDAsMutation,
  useRecalculateSelectedEvaluationsMutation
} = proformaApi;
